import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import TechnicianSelector from './components/TechnicianSelector';
import TechChatWindow from '../technologist-portal/components/TechChatWindow';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';

export default function DoctorChat() {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [activeConv, setActiveConv] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="text-center space-y-4">
          <Icon name="Lock" size={40} className="text-muted-foreground mx-auto" />
          <h2 className="text-lg font-semibold text-foreground">Authentication Required</h2>
          <p className="text-sm text-muted-foreground">Please sign in to access the chat feature.</p>
          <button
            onClick={() => navigate('/login')}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  const doctorName = profile?.full_name || user?.email || 'Doctor';

  return (
    <>
      <Helmet>
        <title>Chat with Technologist | My Doc Refer</title>
        <meta name="description" content="Real-time chat between doctors and diagnostic imaging technologists." />
      </Helmet>
      <div className="min-h-screen bg-background flex flex-col">
        {/* Top bar */}
        <header className="h-16 bg-card border-b border-border flex items-center px-4 sm:px-6 gap-4 flex-shrink-0 z-10">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="ArrowLeft" size={18} />
            <span className="text-sm hidden sm:inline">Dashboard</span>
          </button>

          <Image
            src="/assets/images/Screenshot_2025-12-22_at_12.13.42_AM-1766377944108.png"
            alt="My Doc Refer logo"
            className="h-9 w-auto object-contain"
          />

          <div className="flex-1" />

          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <p className="text-sm font-medium text-foreground leading-none">{doctorName}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{profile?.specialization || 'Doctor'}</p>
            </div>
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="UserCircle" size={18} className="text-primary" />
            </div>
          </div>
        </header>

        {/* Page title bar */}
        <div className="bg-card border-b border-border px-4 sm:px-6 py-3 flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          >
            <Icon name={sidebarOpen ? 'PanelLeftClose' : 'PanelLeftOpen'} size={18} />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="MessageCircle" size={15} className="text-primary" />
            </div>
            <div>
              <h1 className="text-sm font-semibold text-foreground">Chat with Technologist</h1>
              <p className="text-xs text-muted-foreground">Real-time communication with imaging technologists</p>
            </div>
          </div>
          {activeConv && (
            <div className="ml-auto flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-xs text-muted-foreground">Connected</span>
            </div>
          )}
        </div>

        {/* Main layout */}
        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar */}
          {sidebarOpen && (
            <aside className="w-72 border-r border-border bg-card flex-shrink-0 flex flex-col overflow-hidden">
              <TechnicianSelector
                doctorId={user?.id}
                doctorName={doctorName}
                onSelectConversation={setActiveConv}
                activeConvId={activeConv?.id}
              />
            </aside>
          )}

          {/* Chat area */}
          <main className="flex-1 flex flex-col overflow-hidden">
            {activeConv ? (
              <TechChatWindow
                conversation={activeConv}
                currentUser={user?.id}
                currentName={doctorName}
                currentRole="doctor"
              />
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center gap-5 text-center px-6">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <Icon name="MessageCircle" size={30} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Start a Conversation</h2>
                  <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                    Select an existing chat or find a technologist from the sidebar to begin a real-time conversation.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3 w-full max-w-sm">
                  {[
                    { icon: 'Zap', label: 'Real-time', desc: 'Instant delivery' },
                    { icon: 'Shield', label: 'Secure', desc: 'Private channel' },
                    { icon: 'Users', label: 'Direct', desc: 'One-on-one chat' },
                    { icon: 'Clock', label: '24/7', desc: 'Always available' },
                  ]?.map(({ icon, label, desc }) => (
                    <div key={label} className="p-3 bg-muted/50 rounded-lg text-left">
                      <Icon name={icon} size={16} className="text-primary mb-1.5" />
                      <p className="text-xs font-medium text-foreground">{label}</p>
                      <p className="text-xs text-muted-foreground">{desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </>
  );
}
